import * as React from 'react';

import { CompactNumber } from '@blockworks/ui/legacy/numbers';
import { cn } from '@blockworks/ui/utils';
import { Sparklines, SparklinesLine } from 'react-sparklines';

type MarketDataCardProps = {
    className?: string;
    title: JSX.Element | React.ReactNode | string;
    children: JSX.Element | React.ReactNode | string;
    movePercentage?: number;
    sparklineData?: any[];
};

const MarketDataCard = (props: MarketDataCardProps) => {
    const { className, title, children, movePercentage, sparklineData } = props;
    const moveIndicator: 'up' | 'down' = movePercentage && movePercentage >= 0 ? 'up' : 'down';

    return (
        <div className={cn('flex justify-start items-center flex-grow', className)}>
            <div className="flex flex-col justify-start items-start flex-shrink-0 w-36 gap-2">
                <p className="text-xxs text-left uppercase text-dark">
                    <>{title}</>
                </p>
                <div className="flex justify-start items-start flex-grow-0 flex-shrink-0 relative gap-1.5">
                    <p className="flex-grow-0 flex-shrink-0 text-sm text-left text-dark">
                        <>{children}</>
                    </p>
                    {movePercentage !== undefined && (
                        <div className="flex justify-start items-center flex-grow-0 flex-shrink-0 relative gap-1">
                            {moveIndicator === 'up' && (
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="flex-grow-0 flex-shrink-0 w-3 h-3 relative"
                                    preserveAspectRatio="none"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M1.97572 5.82427C1.86324 5.71175 1.80005 5.55917 1.80005 5.40007C1.80005 5.24097 1.86324 5.08838 1.97572 4.97587L5.57572 1.37587C5.68824 1.26338 5.84082 1.2002 5.99992 1.2002C6.15902 1.2002 6.3116 1.26338 6.42412 1.37587L10.0241 4.97587C10.1334 5.08903 10.1939 5.24059 10.1925 5.39791C10.1912 5.55523 10.1281 5.70571 10.0168 5.81696C9.90557 5.9282 9.75508 5.9913 9.59776 5.99267C9.44044 5.99404 9.28888 5.93356 9.17572 5.82427L6.59992 3.24847V10.2001C6.59992 10.3592 6.53671 10.5118 6.42418 10.6243C6.31166 10.7369 6.15905 10.8001 5.99992 10.8001C5.84079 10.8001 5.68818 10.7369 5.57566 10.6243C5.46314 10.5118 5.39992 10.3592 5.39992 10.2001V3.24847L2.82412 5.82427C2.7116 5.93675 2.55902 5.99994 2.39992 5.99994C2.24082 5.99994 2.08824 5.93675 1.97572 5.82427Z"
                                        fill="#14532D"
                                    ></path>
                                </svg>
                            )}
                            {moveIndicator === 'down' && (
                                <svg
                                    width="9"
                                    height="10"
                                    viewBox="0 0 9 10"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="flex-grow-0 flex-shrink-0"
                                    preserveAspectRatio="none"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M8.21691 5.17598C8.32939 5.28849 8.39258 5.44108 8.39258 5.60018C8.39258 5.75928 8.32939 5.91186 8.21691 6.02438L4.61691 9.62438C4.50439 9.73686 4.3518 9.80005 4.19271 9.80005C4.03361 9.80005 3.88102 9.73686 3.76851 9.62438L0.168506 6.02438C0.0592108 5.91122 -0.00126553 5.75965 0.000101089 5.60234C0.00146866 5.44502 0.0645704 5.29453 0.175815 5.18329C0.28706 5.07204 0.437547 5.00894 0.594865 5.00757C0.752183 5.00621 0.903745 5.06668 1.01691 5.17598L3.59271 7.75178L3.59271 0.800178C3.59271 0.641047 3.65592 0.488435 3.76844 0.375913C3.88096 0.263391 4.03358 0.200177 4.19271 0.200177C4.35184 0.200177 4.50445 0.263391 4.61697 0.375913C4.72949 0.488435 4.79271 0.641047 4.79271 0.800178L4.79271 7.75178L7.36851 5.17598C7.48102 5.06349 7.63361 5.00031 7.79271 5.00031C7.9518 5.00031 8.10439 5.06349 8.21691 5.17598Z"
                                        fill="#7F1D1D"
                                    ></path>
                                </svg>
                            )}
                            <p
                                className={cn(
                                    'flex-grow-0 flex-shrink-0 text-sm text-left',
                                    movePercentage >= 0 ? 'text-green-900' : 'text-[#7F1D1D]',
                                )}
                            >
                                <CompactNumber number={movePercentage} fractionDigits={2} /> %
                            </p>
                        </div>
                    )}
                </div>
            </div>
            {sparklineData && (
                <Sparklines data={sparklineData} width={100} height={20}>
                    <SparklinesLine color={(movePercentage || 0) >= 0 ? '#14532D' : '#7F1D1D'} />
                </Sparklines>
            )}
        </div>
    );
};

export default MarketDataCard;
